import * as ents from '@attain-apps/entities'
import { TypeAsserter } from '@attain-apps/type-guards'
import type * as chartJs from 'chart.js'

import * as bar from './bar'
import * as donut from './donut'
import * as line from './line'
import * as scatter from './scatter'
import * as venn from './venn'

/**
 * Transforms a chart configuration object to a format compatible with Chart.js
 *
 * @see {@link chartJs.ChartConfiguration}.
 */
export function toChartJs<TType extends ents.core.display.chart.Type>(
  chart: ents.core.display.chart.Chart<TType>,
): chartJs.ChartConfiguration {
  const { type, data } = chart
  TypeAsserter.assertIsNotNullish(data)
  switch (type) {
    case ents.core.display.chart.Type.Donut:
      return {
        data: donut.getData(data),
        options: donut.getOptions(chart),
        type: 'doughnut',
      }
    case ents.core.display.chart.Type.Line:
      return {
        data: line.getData(data),
        options: line.getOptions(chart),
        type: 'line',
      }
    case ents.core.display.chart.Type.Scatter:
      return {
        data: scatter.getData(data),
        options: scatter.getOptions(chart),
        type: 'scatter',
      }
    case ents.core.display.chart.Type.Bar:
    case ents.core.display.chart.Type.StackedBar:
      return {
        data: bar.getData(data, type),
        options: bar.getOptions(chart, {
          stacked: type === ents.core.display.chart.Type.StackedBar,
        }),
        type: 'bar',
      }
    case ents.core.display.chart.Type.Venn:
      return {
        data: venn.getData(data),
        options: venn.getOptions(chart),
        type: 'venn',
      }
    default: {
      throw new Error('Unsupported chart type')
    }
  }
}
